import React, { useState, useMemo, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Search, Settings2, X, Check, CheckSquare, Square } from 'lucide-react';

export default function ColumnConfigPopover({ columns, visibility, onVisibilityChange }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const buttonRef = useRef(null);
    const menuRef = useRef(null);
    const [coords, setCoords] = useState({ top: 0, left: 0 });

    const updatePosition = () => {
        if (!buttonRef.current) return;
        const rect = buttonRef.current.getBoundingClientRect();
        const menuWidth = 320;
        const margin = 8;

        let left = rect.right - menuWidth;
        if (left < margin) left = margin;

        const top = Math.min(rect.bottom + margin, window.innerHeight - margin);
        setCoords({ top, left, width: menuWidth });
    };

    useLayoutEffect(() => {
        if (!open) return;
        updatePosition();
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);
        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [open]);

    useEffect(() => {
        const handleOutsideClick = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target) &&
                buttonRef.current && !buttonRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        if (open) document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [open]);

    const filteredColumns = useMemo(() => {
        return columns.filter(col =>
            col.headerLabel.toLowerCase().includes(query.toLowerCase()) ||
            col.id.toLowerCase().includes(query.toLowerCase())
        );
    }, [columns, query]);

    const toggleAll = (visible) => {
        const next = { ...visibility };
        columns.forEach(col => {
            if (col.id !== 'symbol') { // Always keep symbol visible
                next[col.id] = visible;
            }
        });
        onVisibilityChange(next);
    };

    return (
        <div className="relative inline-block">
            <button
                ref={buttonRef}
                onClick={() => setOpen(!open)}
                className={`flex items-center gap-2 h-9 px-3 rounded border transition-all duration-200 ${open ? 'bg-blue-50 border-blue-200 text-blue-600 shadow-sm' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                    }`}
                title="Column Settings"
            >
                <Settings2 className="w-4 h-4" />
                <span className="text-sm font-medium">Columns</span>
            </button>

            {open && createPortal(
                <div
                    ref={menuRef}
                    className="z-50 bg-white/90 backdrop-blur-md border border-gray-200 rounded-xl shadow-[0_10px_40px_rgba(0,0,0,0.1)] overflow-hidden flex flex-col animation-in fade-in zoom-in duration-200"
                    style={{
                        position: 'fixed',
                        top: coords.top,
                        left: coords.left,
                        width: coords.width,
                        maxHeight: 'min(500px, 80vh)'
                    }}
                >
                    {/* Header */}
                    <div className="p-3 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
                        <span className="text-sm font-bold text-gray-800">Display Columns</span>
                        <div className="flex gap-2">
                            <button
                                onClick={() => toggleAll(true)}
                                className="text-[10px] font-bold text-blue-600 hover:underline uppercase tracking-tight"
                            >
                                Show All
                            </button>
                            <button
                                onClick={() => toggleAll(false)}
                                className="text-[10px] font-bold text-gray-500 hover:underline uppercase tracking-tight"
                            >
                                Hide All
                            </button>
                        </div>
                    </div>

                    {/* Search */}
                    <div className="p-2 border-b border-gray-100">
                        <div className="relative">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                            <input
                                autoFocus
                                className="w-full h-8 pl-8 pr-3 text-sm bg-gray-100/50 border-none rounded-lg focus:ring-2 focus:ring-blue-500/20 transition-all outline-none"
                                placeholder="Search metrics..."
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                            />
                        </div>
                    </div>

                    {/* List */}
                    <div className="flex-1 overflow-auto p-1.5 custom-scrollbar">
                        {filteredColumns.map((col) => {
                            const isVisible = visibility[col.id] !== false;
                            const isRequired = col.id === 'symbol';

                            return (
                                <button
                                    key={col.id}
                                    disabled={isRequired}
                                    onClick={() => onVisibilityChange({
                                        ...visibility,
                                        [col.id]: !isVisible
                                    })}
                                    className={`w-full flex items-center gap-3 px-2 py-2 rounded-lg transition-colors text-left ${isRequired ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100/80 active:bg-gray-200/50'
                                        }`}
                                >
                                    <div className={`shrink-0 w-4 h-4 rounded border flex items-center justify-center transition-all ${isVisible
                                            ? 'bg-blue-600 border-blue-600 text-white'
                                            : 'bg-white border-gray-300 text-transparent'
                                        }`}>
                                        <Check className={`w-3 h-3 stroke-[3px] ${isVisible ? 'scale-100' : 'scale-0'} transition-transform`} />
                                    </div>
                                    <div className="flex flex-col min-w-0">
                                        <span className="text-sm font-medium text-gray-700 truncate">{col.headerLabel}</span>
                                        {query && (
                                            <span className="text-[10px] text-gray-400 font-mono truncate lowercase">{col.id}</span>
                                        )}
                                    </div>
                                </button>
                            );
                        })}

                        {filteredColumns.length === 0 && (
                            <div className="py-8 text-center text-gray-400 text-sm italic">
                                No matching columns found
                            </div>
                        )}
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}
